import Intercom from '@intercom/messenger-js-sdk';
import config from '@/config/widgets';
import { request } from '@/api/request';
import type { TrialResponse } from '@/modules/trial/api';
import type AnalyticsModule from './analyticsModule';
import type { SetUserPropertiesParams } from './analyticsModule';

// Cache for intercom JWT
let intercomJWTPromise: Promise<string> | null = null;
// User ID for which we have a cached JWT
let cachedForUserID: string | null = null;

// Get the intercom JWT for a user
// If we have a cached JWT for the same user, return it
// Otherwise, fetch and cache
// NB: intercom JWT is valid for 24 hrs, and our sessions are valid for 12 hrs
// therefore we don't need to check if the JWT is still valid before fetching a new one
const getIntercomJWT = (userID: string) => {
  if (intercomJWTPromise && cachedForUserID === userID) {
    return intercomJWTPromise;
  }
  intercomJWTPromise = request<{ jwt: string }>('GET', '/user/intercom/jwt').then(
    (r) => r.data.jwt,
  );
  cachedForUserID = userID;
  return intercomJWTPromise;
};

const setUserData = async (user: any) => {
  if (user && window.intercomSettings) {
    window.intercomSettings.user_id = user.userID;
    window.intercomSettings.name = `${user.firstName} ${user.lastName}`;
    window.intercomSettings.phone = user.phone;
    window.intercomSettings.email = user.email;

    window.intercomSettings.createdAt = user.createdAt;
    window.intercomSettings.activatedAt = user.activatedAt;
    window.intercomSettings.status = user.status;
    window.intercomSettings.role = user.role;
    window.intercomSettings.team = user.team;

    window.intercomSettings.godUser = `${config.appUrl}/zeus/users/${user.userID}`;

    const jwt = await getIntercomJWT(user.userID);
    Intercom({
      app_id: config.intercomAppID,
      api_base: 'https://api-iam.eu.intercom.io',
      region: 'eu',
      user_id: user.userID,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      created_at: user.createdAt,
      intercom_user_jwt: jwt,
    });
  }
};

const setOrgData = (org: any, status: any, trial?: TrialResponse) => {
  if (org && window.intercomSettings) {
    window.intercomSettings.company = {
      name: org.name,
      company_id: org.orgID,
      website: org.website,
      isSupplier: org.isSupplier,
      isClient: org.isClient,
      paid: org.paid,
      assessmentProgress: status ? status.assessmentProgress : '',
      initialAssessmentComplete: status ? status.initialAssessmentComplete : '',
      scopingComplete: status ? status.scopingComplete : '',
      trialType: trial ? trial.trialType : '',
    };

    window.intercomSettings.avatar = {
      type: 'avatar',
      image_url: org.logoUrl,
    };
  }
};

/* eslint class-methods-use-this: 0 */
export default class implements AnalyticsModule {
  reset() {
    // NOOP
  }

  logError() {
    // NOOP
  }

  constructor() {
    window.intercomSettings = {
      app_id: config.intercomAppID,
      api_base: 'https://api-iam.eu.intercom.io',
    };
    Intercom({
      app_id: config.intercomAppID,
      region: 'eu',
    });
  }

  trackEvent(eventName: string, metaObject: Record<string, any>) {
    window.Intercom('trackEvent', eventName, metaObject);
  }

  trackView() {
    window.Intercom('update');
  }

  async setUserProperties({ user, org, status, trial }: SetUserPropertiesParams) {
    await setUserData(user);
    setOrgData(org, status, trial);
    window.Intercom('update', window.intercomSettings);
  }
}
